import { useEffect, useId, useMemo, useState } from 'react';
import { Input } from '@gokrew/ui';
import { cityKey, loadQuebecCities, matchCities } from './crm-cities';
import styles from './Crm.module.css';

/** A city field that suggests Quebec municipalities as you type. */
export const CrmCityInput = ({
  value,
  onChange,
  quebec,
}: {
  value: string;
  /** `picked` when a suggested municipality was chosen. */
  onChange: (city: string, picked?: boolean) => void;
  quebec: boolean;
}) => {
  const listId = useId();
  const [names, setNames] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (quebec) void loadQuebecCities().then(setNames);
  }, [quebec]);
  const cities = useMemo(
    () => names.map((name) => ({ name, key: cityKey(name) })),
    [names]
  );
  const matches = useMemo(
    () =>
      quebec ? matchCities(cities, value).filter((name) => name !== value) : [],
    [quebec, cities, value]
  );
  const shown = open && matches.length > 0;
  const pick = (name: string) => {
    onChange(name, true);
    setOpen(false);
  };
  return (
    <div className={styles.cityField}>
      <Input
        maxLength={100}
        role="combobox"
        aria-expanded={shown}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (!shown) return;
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((index) => (index + 1) % matches.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => (index - 1 + matches.length) % matches.length);
          } else if (event.key === 'Enter') {
            event.preventDefault();
            pick(matches[active]);
          } else if (event.key === 'Escape') setOpen(false);
        }}
      />
      {shown && (
        <ul id={listId} role="listbox" className={styles.citySuggestions}>
          {matches.map((name, index) => (
            <li
              key={name}
              role="option"
              aria-selected={index === active}
              className={index === active ? styles.cityActive : ''}
              // mousedown, so the pick lands before the input's blur.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(name);
              }}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
